import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, Trash2, UserPlus } from 'lucide-react';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { PageHeader } from '../components/ui/PageHeader.tsx';
import { Button } from '../components/ui/Button.tsx';
import { Badge } from '../components/ui/Badge.tsx';
import { Modal, ModalFooter } from '../components/ui/Modal.tsx';
import { Input } from '../components/ui/Input.tsx';
import { SkeletonRows } from '../components/ui/Skeleton.tsx';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.tsx';
import { useToast } from '../components/ui/Toast.tsx';
import { EventStatusBadge } from '../components/events/EventStatusBadge.tsx';

// ─── Types ────────────────────────────────────────────────────────────────────

interface EventItem {
  id: string;
  name: string;
  status: string;
  startsAt: string;
  endsAt: string | null;
  locationName: string | null;
  maxCapacity: number | null;
}

interface TourEventItem {
  id: string;
  sequenceOrder: number;
  event: EventItem;
}

interface TourDetail {
  id: string;
  name: string;
  status: string;
  passEnabled: boolean;
  createdAt: string;
  events: TourEventItem[];
  enrollmentCount: number;
}

interface EnrollmentItem {
  id: string;
  customerId: string;
  status: string;
  enrolledAt: string;
  totalEvents: number;
  completedEvents: number;
  customerFirstName: string | null;
  customerLastName: string | null;
  customerEmail: string | null;
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' });
}

function enrollmentStatusVariant(status: string) {
  if (status === 'active') return 'success' as const;
  if (status === 'completed') return 'info' as const;
  if (status === 'cancelled') return 'danger' as const;
  return 'default' as const;
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function TourDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  const [showAddEvent, setShowAddEvent] = useState(false);
  const [addEventId, setAddEventId] = useState('');
  const [removeEventTarget, setRemoveEventTarget] = useState<TourEventItem | null>(null);
  const [showAddEnrollment, setShowAddEnrollment] = useState(false);
  const [enrollCustomerId, setEnrollCustomerId] = useState('');

  const { data: tour, isLoading } = useQuery<TourDetail>({
    queryKey: ['tour-detail', id],
    queryFn: () =>
      api.get(`/api/v1/events/tours/${id}`).then((r) => r.data),
    enabled: !!id,
  });

  const { data: enrollments = [] } = useQuery<EnrollmentItem[]>({
    queryKey: ['tour-enrollments', id],
    queryFn: () =>
      api.get(`/api/v1/events/tours/${id}/enrollments`).then((r) => r.data),
    enabled: !!id && !!tour?.passEnabled,
  });

  const addEventMutation = useMutation({
    mutationFn: (eventId: string) =>
      api.post(`/api/v1/events/tours/${id}/events`, { eventId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tour-detail', id] });
      setShowAddEvent(false);
      setAddEventId('');
      toast.success('Event added to tour');
    },
    onError: (err) => toast.error(extractError(err, 'Error adding event')),
  });

  const removeEventMutation = useMutation({
    mutationFn: (eventId: string) =>
      api.delete(`/api/v1/events/tours/${id}/events/${eventId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tour-detail', id] });
      setRemoveEventTarget(null);
      toast.success('Stop removed from tour');
    },
    onError: (err) => toast.error(extractError(err, 'Error removing stop')),
  });

  const enrollMutation = useMutation({
    mutationFn: (customerId: string) =>
      api.post(`/api/v1/events/tours/${id}/enrollments`, { customerId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tour-enrollments', id] });
      qc.invalidateQueries({ queryKey: ['tour-detail', id] });
      setShowAddEnrollment(false);
      setEnrollCustomerId('');
      toast.success('Customer enrolled in tour');
    },
    onError: (err) => toast.error(extractError(err, 'Error enrolling customer')),
  });

  if (isLoading) {
    return (
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <SkeletonRows count={4} cols={3} />
      </div>
    );
  }

  if (!tour) {
    return <p className="text-sm text-zinc-500">Tour not found.</p>;
  }

  const sortedEvents = [...(tour.events ?? [])].sort(
    (a, b) => a.sequenceOrder - b.sequenceOrder,
  );

  return (
    <div>
      <PageHeader
        title={tour.name}
        action={
          <Button variant="ghost" size="sm" onClick={() => navigate('/events/tours')}>
            <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back to list
          </Button>
        }
      />

      {/* Header info */}
      <div className="flex items-center gap-3 mb-6">
        <Badge variant={tour.status === 'active' ? 'success' : 'default'}>{tour.status}</Badge>
        {tour.passEnabled && <Badge variant="info">Tour pass enabled</Badge>}
        <span className="text-sm text-zinc-400">{tour.enrollmentCount} pass enrollees</span>
      </div>

      {/* Stops section */}
      <section className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-zinc-700 uppercase tracking-wider">Stops</h2>
          <Button size="sm" variant="secondary" onClick={() => setShowAddEvent(true)}>
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Add event
          </Button>
        </div>

        {sortedEvents.length === 0 ? (
          <p className="text-sm text-zinc-400 text-center py-8 bg-white border border-zinc-200 rounded-xl">
            No stops. Add the first event.
          </p>
        ) : (
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-100">
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider w-12">
                    #
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Event
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 w-16" />
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {sortedEvents.map((te) => (
                  <tr key={te.id} className="hover:bg-zinc-50 group">
                    <td className="px-4 py-3 tabular-nums text-zinc-400">{te.sequenceOrder}</td>
                    <td className="px-4 py-3">
                      <button
                        className="font-medium text-zinc-900 hover:underline text-left"
                        onClick={() => navigate(`/events/${te.event.id}`)}
                      >
                        {te.event.name}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-zinc-500 text-xs">
                      {formatDate(te.event.startsAt)}
                    </td>
                    <td className="px-4 py-3">
                      <EventStatusBadge status={te.event.status} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => setRemoveEventTarget(te)}
                          className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-md"
                          title="Remove stop"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Pass enrollees section (only if passEnabled) */}
      {tour.passEnabled && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-zinc-700 uppercase tracking-wider">
              Pass enrollees
            </h2>
            <Button size="sm" variant="secondary" onClick={() => setShowAddEnrollment(true)}>
              <UserPlus className="w-3.5 h-3.5 mr-1.5" /> Enroll customer
            </Button>
          </div>

          {enrollments.length === 0 ? (
            <p className="text-sm text-zinc-400 text-center py-8 bg-white border border-zinc-200 rounded-xl">
              No pass enrollees.
            </p>
          ) : (
            <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-100">
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                      Progress
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                      Enrolled on
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {enrollments.map((en) => {
                    const displayName =
                      [en.customerFirstName, en.customerLastName].filter(Boolean).join(' ') ||
                      en.customerEmail ||
                      en.customerId;
                    const pct =
                      en.totalEvents > 0
                        ? Math.round((en.completedEvents / en.totalEvents) * 100)
                        : 0;
                    return (
                      <tr key={en.id} className="hover:bg-zinc-50">
                        <td className="px-4 py-3">
                          <div className="font-medium text-zinc-900">{displayName}</div>
                          {en.customerEmail && displayName !== en.customerEmail && (
                            <div className="text-xs text-zinc-400">{en.customerEmail}</div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={enrollmentStatusVariant(en.status)}>{en.status}</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-zinc-100 rounded-full h-1.5 max-w-[80px]">
                              <div
                                className="bg-zinc-700 h-1.5 rounded-full"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <span className="text-xs text-zinc-500 tabular-nums">
                              {en.completedEvents}/{en.totalEvents}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-zinc-500 text-xs">
                          {formatDate(en.enrolledAt)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* Add event modal */}
      <Modal open={showAddEvent} onClose={() => setShowAddEvent(false)} title="Add event to tour">
        <div className="space-y-4">
          <Input
            label="Event ID (UUID) *"
            value={addEventId}
            onChange={(e) => setAddEventId(e.target.value)}
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
          />
          {addEventMutation.isError && (
            <p className="text-xs text-red-500">{extractError(addEventMutation.error)}</p>
          )}
        </div>
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={() => setShowAddEvent(false)}>
            Cancel
          </Button>
          <Button
            loading={addEventMutation.isPending}
            disabled={!addEventId.trim()}
            onClick={() => addEventMutation.mutate(addEventId.trim())}
          >
            Add
          </Button>
        </ModalFooter>
      </Modal>

      {/* Remove event confirm */}
      <ConfirmDialog
        open={!!removeEventTarget}
        title="Remove stop?"
        message={`"${removeEventTarget?.event.name}" will be removed from the tour.`}
        confirmLabel="Remove"
        variant="danger"
        loading={removeEventMutation.isPending}
        onConfirm={() =>
          removeEventTarget && removeEventMutation.mutate(removeEventTarget.event.id)
        }
        onCancel={() => setRemoveEventTarget(null)}
      />

      {/* Add enrollment modal */}
      <Modal
        open={showAddEnrollment}
        onClose={() => setShowAddEnrollment(false)}
        title="Enroll customer in tour"
      >
        <div className="space-y-4">
          <Input
            label="Customer ID (UUID) *"
            value={enrollCustomerId}
            onChange={(e) => setEnrollCustomerId(e.target.value)}
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
          />
          {enrollMutation.isError && (
            <p className="text-xs text-red-500">{extractError(enrollMutation.error)}</p>
          )}
        </div>
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={() => setShowAddEnrollment(false)}>
            Cancel
          </Button>
          <Button
            loading={enrollMutation.isPending}
            disabled={!enrollCustomerId.trim()}
            onClick={() => enrollMutation.mutate(enrollCustomerId.trim())}
          >
            Enroll
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
